// ---------------------------------------------------------------------------
// telephoneEncounterService
//
// Frontend data layer for telephone encounters (TEs).
//
// Reference reads are real:
//   - reason presets come from the company reason list (constant for now)
//   - providers are read from the EHR cache in Firestore
//     (`EhrCache/{companyId}/providers`), the same mirror the rest of the app
//     reads from (see `ehrService.fetchCachedEncounters`).
//   - the assign-to care-team is read from `EhrCache/{companyId}/staff`, the
//     eCW "Assign To" roster mirrored nightly by `EhrSyncService.syncStaffList`.
//     Real-time typeahead search beyond that preloaded set hits the backend
//     `assignable-staff` route on demand (`getAssignableStaff`).
//
// Backend-backed actions:
//   - `generateTelephoneEncounterText` calls the dedicated generation route
//     that turns a call transcript into a 1-3 sentence encounter message.
//   - `findOpenTelephoneEncounters` checks ECW (via the backend) for an open
//     TE already on the patient, so the UI can offer "edit vs create new".
//   - `pushToEhr` writes the reviewed TE to ECW.
//
// The TE record itself is held in a module-level Map for the duration of the
// editing session — ECW is the system of record once pushed. Field edits,
// action-taken entries and assignment all mutate that local record; the EHR
// push/close ops are what cross the wire (`// TODO(zatanna)` where the vendor
// op is still pending).
// ---------------------------------------------------------------------------

import {collection, getDocs, query, limit} from 'firebase/firestore';
import {db} from '../firebase';
import {request} from './apiClient';
import {generateUUIDv7} from '../utils/uuidv7';
import {logger, LogFlow, describeError} from './logger';

// Scoped logger for telephone-encounter flows. We log ids, status, reason
// CODES (not text), counts, and durations — never patient names, the message
// body, or transcripts (PHI).
const log = logger.scope(LogFlow.PhoneEncounter);
import {TELEPHONE_ENCOUNTER_REASONS} from '../components/telephone/telephoneEncounterReasons';
import {fetchOnboardingFacilities} from './onboardingService';
import type {
  CareTeamMember,
  FacilityOption,
  ReasonPreset,
  TelephoneEncounter,
  TelephoneEncounterAction,
  TranslatorInfo,
  OpenTelephoneEncounterSummary,
} from '../types/telephoneEncounter';

const EHR_PREFIX = '/api/v1/ehr';

// ── In-memory store ────────────────────────────────────────────────────────

const store = new Map<string, TelephoneEncounter>();

function nowIso(): string {
  return new Date().toISOString();
}

function clone(te: TelephoneEncounter): TelephoneEncounter {
  return {...te, actions: te.actions.map(action => ({...action}))};
}

function titleCase(value: any): string {
  if (!value) return '';
  return String(value)
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function emptyTelephoneEncounter(
  overrides: Partial<TelephoneEncounter> = {},
): TelephoneEncounter {
  const timestamp = nowIso();
  return {
    id: generateUUIDv7(),
    patientId: null,
    ehrPatientId: null,
    patientName: null,
    companyId: null,
    facilityId: null,
    facilityName: null,
    reasonCode: null,
    reasonText: '',
    providerId: null,
    providerName: null,
    message: '',
    messageSourceCallId: null,
    assignedToId: null,
    assignedToName: null,
    status: 'draft',
    actions: [],
    translator: null,
    sourceCallId: null,
    ehrEncounterId: null,
    createdBy: null,
    createdByName: null,
    createdAt: timestamp,
    updatedAt: timestamp,
    closedAt: null,
    messageEditedByName: null,
    messageEditedAt: null,
    ...overrides,
  };
}

// ── Reference reads ────────────────────────────────────────────────────────

export async function getReasonPresets(): Promise<ReasonPreset[]> {
  // The reason list is a fixed company catalog today (no synced source). When
  // a per-company catalog is mirrored, read it here and fall back to this.
  return TELEPHONE_ENCOUNTER_REASONS.map(preset => ({...preset}));
}

// The facilities the current user's company can file a telephone encounter
// under. Reads the same backend list onboarding uses (`/onboarding/facilities`).
// Returns [] on any failure so the picker degrades to "keep the default
// facility" rather than blocking the create flow.
export async function getFacilities(): Promise<FacilityOption[]> {
  try {
    const raw = await fetchOnboardingFacilities();
    const rows = Array.isArray(raw) ? raw : (raw?.facilities ?? []);
    const facilities = rows
      .map(
        (row: any): FacilityOption => ({
          id: String(row?.id ?? row?.facility_id ?? ''),
          name:
            (row?.facility_name && String(row.facility_name).trim()) ||
            (row?.name && String(row.name).trim()) ||
            'Unnamed facility',
        }),
      )
      .filter((f: FacilityOption) => f.id);
    facilities.sort((a: FacilityOption, b: FacilityOption) =>
      a.name.localeCompare(b.name),
    );
    return facilities;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[telephoneEncounterService] facilities load failed', err);
    }
    return [];
  }
}

// Map one `EhrCache/{companyId}/providers` doc onto a selectable member.
function mapProviderDoc(docId: string, data: any): CareTeamMember {
  const name = (data?.name && String(data.name).trim()) || 'Unnamed';
  const isResource = data?.kind === 'resource';
  const role = isResource
    ? data?.specialty
      ? String(data.specialty)
      : 'Resource'
    : data?.specialty
      ? String(data.specialty)
      : 'Provider';
  return {
    id: String(data?.providerId || data?.ehr_source_id || docId),
    name,
    role,
    kind: isResource ? 'resource' : 'provider',
  };
}

// Read every provider/resource the EHR sync mirrored for this company. A
// single read (capped) is plenty — provider rosters are small relative to
// patients. Returns [] when there's no company or the cache is empty so the
// pickers degrade to an empty (but functional) list rather than throwing.
async function readCachedProviders(
  companyId: string | null | undefined,
): Promise<CareTeamMember[]> {
  if (!companyId) return [];
  const ref = collection(db, 'EhrCache', companyId, 'providers');
  const snapshot = await getDocs(query(ref, limit(1000)));
  const members = snapshot.docs.map(d => mapProviderDoc(d.id, d.data()));
  members.sort((a, b) => a.name.localeCompare(b.name));
  return members;
}

// Providers to attribute the encounter to — clinicians only (`kind` provider
// or unset). Resources (pools, school clinics) are excluded here.
export async function getProviders(
  companyId: string | null = null,
): Promise<CareTeamMember[]> {
  const members = await readCachedProviders(companyId);
  return members.filter(member => member.kind !== 'resource');
}

// Map one backend assignable-staff row (Zatanna `fetch-staff-list` /
// `search-staff-list`) onto a selectable member. The backend already
// normalizes the eCW staff row to `{ id, name, role, ... }`; `id` is the
// `assignedToId` and `name` the `assignedToName` used on push.
function mapStaffRow(row: any): CareTeamMember {
  return {
    id: String(row?.id ?? ''),
    name: (row?.name && String(row.name).trim()) || 'Unnamed',
    role: (row?.role && String(row.role).trim()) || 'Staff',
  };
}

// Live server-side staff search for the assign-to picker's typeahead. Hits
// Zatanna's `search-staff-list` (when `query` is set) / `fetch-staff-list` via
// the backend. This is on-demand only — the picker's initial roster comes from
// the nightly-synced Firestore cache (`getCareTeam`); this widens it as the
// registrar types. Returns [] when the backend has no array to give.
export async function getAssignableStaff(
  query: string | null = null,
): Promise<CareTeamMember[]> {
  const data = await request(
    `${EHR_PREFIX}/telephone-encounters/assignable-staff`,
    {
      ...(query && query.trim() ? {query: {query: query.trim()}} : {}),
    },
  );
  if (!Array.isArray(data)) return [];
  const members = data.map(mapStaffRow).filter(member => member.id);
  members.sort((a, b) => a.name.localeCompare(b.name));
  return members;
}

// Map one `EhrCache/{companyId}/staff` doc (mirrored nightly from eCW's
// "Assign To" roster by `EhrSyncService.syncStaffList`) onto a selectable
// member. `staff_id` is the `assignedToId`; `name` is the `assignedToName`.
function mapStaffDoc(docId: string, data: any): CareTeamMember {
  return {
    id: String(data?.staff_id || docId),
    name: (data?.name && String(data.name).trim()) || 'Unnamed',
    role: (data?.role && String(data.role).trim()) || 'Staff',
  };
}

// Read the nightly-synced staff roster straight from Firestore. The staff list
// changes rarely, so it's persisted on the nightly full sync and read here —
// no per-open Zatanna call. Returns [] when there's no company or the roster
// hasn't been synced yet.
async function readCachedStaff(
  companyId: string | null | undefined,
): Promise<CareTeamMember[]> {
  if (!companyId) return [];
  const ref = collection(db, 'EhrCache', companyId, 'staff');
  const snapshot = await getDocs(query(ref, limit(5000)));
  const members = snapshot.docs.map(d => mapStaffDoc(d.id, d.data()));
  members.sort((a, b) => a.name.localeCompare(b.name));
  return members;
}

// Assignable care-team — everyone the encounter can be routed to (clinicians,
// nurses, front-desk, staff pools). Read from the nightly-synced staff cache;
// falls back to the cached providers/resources when the staff roster hasn't
// been synced yet (or the read fails) so the picker degrades to a usable list
// rather than empty. Real-time search beyond this preloaded set goes through
// `getAssignableStaff` (the debounced typeahead in CareTeamSelect).
export async function getCareTeam(
  companyId: string | null = null,
): Promise<CareTeamMember[]> {
  try {
    const staff = await readCachedStaff(companyId);
    if (staff.length > 0) return staff;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[telephoneEncounterService] cached staff load failed', err);
    }
  }
  return readCachedProviders(companyId);
}

// ── Encounter reads ────────────────────────────────────────────────────────

export async function getTelephoneEncounter(
  id: string,
): Promise<TelephoneEncounter | null> {
  const found = store.get(id);
  return found ? clone(found) : null;
}

// Ask the backend whether the patient already has an open telephone encounter
// in ECW. Drives the "edit the open one vs create a new one" prompt. Resolves
// to [] on any failure (network, EHR not configured, route not yet wired) so
// the caller falls through to the create flow rather than blocking the user.
export async function findOpenTelephoneEncounters(
  ehrPatientId: string | null | undefined,
): Promise<OpenTelephoneEncounterSummary[]> {
  if (!ehrPatientId) return [];
  try {
    const data = await request(
      `${EHR_PREFIX}/patients/${ehrPatientId}/telephone-encounters`,
      {query: {status: 'open'}},
    );
    const open = Array.isArray(data)
      ? (data as OpenTelephoneEncounterSummary[])
      : [];
    log.info('open_te_check_completed', {
      ehr_patient_id: ehrPatientId,
      open_count: open.length,
    });
    return open;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[telephoneEncounterService] open-TE check failed', err);
    }
    log.warn('open_te_check_failed', {
      ehr_patient_id: ehrPatientId,
      ...describeError(err),
    });
    return [];
  }
}

// ── Message generation (backend) ────────────────────────────────────────────

interface GenerateTelephoneEncounterTextInput {
  callId?: string | null;
  messages?: any[] | null;
  patientName?: string | null;
  reasonText?: string | null;
  language?: string | null;
  // The Opalite telephone-encounter id, stamped into the generated message so
  // the ECW record references the Opalite record (message only).
  opaliteId?: string | null;
  // What to generate: the encounter message (default) or the action-taken note.
  kind?: 'message' | 'action_taken';
}

// Turn the originating call into a short telephone-encounter message (default)
// or action-taken note. Returns '' on failure so the registrar can still type
// it by hand.
export async function generateTelephoneEncounterText(
  input: GenerateTelephoneEncounterTextInput,
): Promise<string> {
  const kind = input.kind || 'message';
  const startedAt = Date.now();
  log.info('generate_text_started', {
    kind,
    has_call_id: Boolean(input.callId),
    message_count: Array.isArray(input.messages) ? input.messages.length : 0,
    has_language: Boolean(input.language),
  });
  try {
    const data = await request(`${EHR_PREFIX}/telephone-encounters/generate`, {
      method: 'POST',
      body: JSON.stringify({
        ...(input.callId ? {callId: input.callId} : {}),
        ...(Array.isArray(input.messages) ? {messages: input.messages} : {}),
        ...(input.patientName ? {patientName: input.patientName} : {}),
        ...(input.reasonText ? {reasonText: input.reasonText} : {}),
        ...(input.language ? {language: input.language} : {}),
        ...(input.opaliteId ? {opaliteId: input.opaliteId} : {}),
        ...(input.kind ? {kind: input.kind} : {}),
      }),
    });
    const message = String(data?.message || '').trim();
    log.info('generate_text_completed', {
      kind,
      generated_length: message.length,
      produced_text: message.length > 0,
      duration_ms: Date.now() - startedAt,
    });
    return message;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[telephoneEncounterService] generation failed', err);
    }
    log.error('generate_text_failed', {
      kind,
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    return '';
  }
}

// ── Voice edit (backend) ────────────────────────────────────────────────────

interface EditTelephoneEncounterTextInput {
  // The on-screen draft the spoken instruction should be applied to.
  currentText: string;
  // What the clinician said into the mic — a command, new content, or both.
  instruction: string;
  patientName?: string | null;
  language?: string | null;
  // Which field is being edited; only changes the field label in the prompt.
  kind?: 'message' | 'action_taken';
  // `edit` (default) applies the instruction to currentText; `compose` treats
  // the transcript as the field's dictated content (a provider reading lab
  // results into an empty Message) and formats it verbatim-preserving.
  mode?: 'edit' | 'compose';
}

// Apply a spoken voice-edit instruction to a telephone-encounter draft (the
// Message or an Action Taken note) and return the full revised text. This is
// the TE analog of the scribe note's editNoteWithDictation: the clinician can
// say "remove the line about the telephone encounter" and have it applied,
// instead of the words being appended verbatim. Throws on failure so the caller
// can surface a toast and leave the existing draft untouched (returning '' would
// silently wipe the field).
export async function editTelephoneEncounterText(
  input: EditTelephoneEncounterTextInput,
): Promise<string> {
  const instruction = (input.instruction || '').trim();
  // Nothing spoken — keep the draft exactly as-is without a round trip.
  if (!instruction) return input.currentText ?? '';

  const kind = input.kind || 'message';
  const mode = input.mode || 'edit';
  const startedAt = Date.now();
  log.info('edit_text_started', {
    kind,
    mode,
    instruction_length: instruction.length,
    current_length: (input.currentText || '').length,
    has_language: Boolean(input.language),
  });
  try {
    const data = await request(`${EHR_PREFIX}/telephone-encounters/edit-text`, {
      method: 'POST',
      body: JSON.stringify({
        currentText: input.currentText ?? '',
        instruction,
        ...(input.patientName ? {patientName: input.patientName} : {}),
        ...(input.language ? {language: input.language} : {}),
        ...(input.kind ? {kind: input.kind} : {}),
        ...(input.mode ? {mode: input.mode} : {}),
      }),
    });
    const message = String(data?.message ?? '');
    log.info('edit_text_completed', {
      kind,
      mode,
      revised_length: message.length,
      duration_ms: Date.now() - startedAt,
    });
    return message;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[telephoneEncounterService] voice edit failed', err);
    }
    log.error('edit_text_failed', {
      kind,
      mode,
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    throw err;
  }
}

// ── Patient-friendly simplify (backend) ─────────────────────────────────────

// The label line that opens the plain-language block appended under the
// clinical Message. Also the marker `composeMessageWithSimplifiedBlock` uses
// to REPLACE a previous block on re-simplify instead of stacking duplicates.
// Pushed to ECW as part of the Message, so the nurse calling the patient sees
// both the clinical text and the read-aloud version.
export const SIMPLIFIED_BLOCK_LABEL =
  'Patient-friendly explanation (read to patient):';

// Strip a previously appended patient-friendly block (everything from the
// label line to the end of the text). The block is always appended last, so
// truncating at the label is safe.
export function stripSimplifiedBlock(text: string): string {
  const idx = (text || '').indexOf(SIMPLIFIED_BLOCK_LABEL);
  if (idx === -1) return text || '';
  return (text || '').slice(0, idx).replace(/\s+$/, '');
}

// Compose the full Message field: clinical text + labeled plain-language
// block. Re-running simplify replaces the previous block.
export function composeMessageWithSimplifiedBlock(
  clinicalText: string,
  simplifiedText: string,
): string {
  const base = stripSimplifiedBlock(clinicalText).trim();
  const simplified = (simplifiedText || '').trim();
  if (!simplified) return base;
  return base
    ? `${base}\n\n${SIMPLIFIED_BLOCK_LABEL}\n${simplified}`
    : `${SIMPLIFIED_BLOCK_LABEL}\n${simplified}`;
}

interface SimplifyTelephoneEncounterMessageInput {
  // The Message text to rewrite (may include a previous patient-friendly
  // block — it's stripped before the rewrite so simplify never re-simplifies
  // its own output).
  message: string;
  patientName?: string | null;
}

// Rewrite the clinical Message at a 3rd-5th grade reading level and return the
// FULL new Message: clinical text with the labeled patient-friendly block
// appended. Throws on failure so the caller can toast and leave the draft
// untouched.
export async function simplifyTelephoneEncounterMessage(
  input: SimplifyTelephoneEncounterMessageInput,
): Promise<string> {
  const clinical = stripSimplifiedBlock(input.message || '').trim();
  if (!clinical) return input.message || '';

  const startedAt = Date.now();
  log.info('simplify_started', {source_length: clinical.length});
  try {
    const data = await request(`${EHR_PREFIX}/telephone-encounters/simplify`, {
      method: 'POST',
      body: JSON.stringify({
        message: clinical,
        ...(input.patientName ? {patientName: input.patientName} : {}),
      }),
    });
    const simplified = String(data?.message ?? '').trim();
    log.info('simplify_completed', {
      simplified_length: simplified.length,
      duration_ms: Date.now() - startedAt,
    });
    if (!simplified) return input.message || '';
    return composeMessageWithSimplifiedBlock(clinical, simplified);
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[telephoneEncounterService] simplify failed', err);
    }
    log.error('simplify_failed', {
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    throw err;
  }
}

// ── Encounter creation ─────────────────────────────────────────────────────

interface CreateBlankDraftInput {
  companyId?: string | null;
  facilityId?: string | null;
  facilityName?: string | null;
  patientId?: string | null;
  ehrPatientId?: string | null;
  patientName?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
}

interface AdoptOpenTelephoneEncounterInput extends CreateBlankDraftInput {
  // The just-completed call this encounter is being worked from. Carried over
  // from the create-mode draft so the adopted encounter can generate its
  // Action Taken note from the call transcript and tag the Opalite session.
  sourceCallId?: string | null;
  translator?: TranslatorInfo | null;
}

export async function createBlankDraft(
  input: CreateBlankDraftInput = {},
): Promise<TelephoneEncounter> {
  const draft = emptyTelephoneEncounter({
    companyId: input.companyId ?? null,
    facilityId: input.facilityId ?? null,
    facilityName: input.facilityName ?? null,
    patientId: input.patientId ?? null,
    ehrPatientId: input.ehrPatientId ?? null,
    patientName: input.patientName ?? null,
    createdBy: input.createdBy ?? null,
    createdByName: input.createdByName ?? null,
  });
  store.set(draft.id, draft);
  return clone(draft);
}

// Build a draft pre-populated from a completed company phone call. The
// reason/message are left for the registrar (the reason is a picked preset,
// and the message is generated separately via `generateTelephoneEncounterText`
// once the draft is open). The interpreter/language is captured automatically
// so a registrar never types an interpreter ID by hand.
export async function createDraftFromCall(
  call: any,
  input: CreateBlankDraftInput = {},
): Promise<TelephoneEncounter> {
  const language = titleCase(call?.language || '');
  const dialect = titleCase(call?.dialect || '');

  const draft = emptyTelephoneEncounter({
    companyId: input.companyId ?? call?.company_id ?? null,
    facilityId: input.facilityId ?? call?.facility_id ?? null,
    facilityName: input.facilityName ?? call?.facility_name ?? null,
    patientId: input.patientId ?? call?.patient_id ?? null,
    ehrPatientId: input.ehrPatientId ?? call?.ehr_patient_id ?? null,
    patientName: input.patientName ?? call?.patient_name ?? null,
    createdBy: input.createdBy ?? null,
    createdByName: input.createdByName ?? null,
    sourceCallId: call?.id ?? null,
    translator: language
      ? {
          translatorId: 'OPALITE',
          language,
          dialect: dialect || null,
          vendor: 'Opalite',
        }
      : null,
  });
  store.set(draft.id, draft);
  return clone(draft);
}

// Adopt an existing open telephone encounter (returned by the open-TE check)
// into the local editing session so the detail view can render it in EDIT mode
// (Action Taken thread, no reason/message composer).
export async function adoptOpenTelephoneEncounter(
  summary: OpenTelephoneEncounterSummary,
  input: AdoptOpenTelephoneEncounterInput = {},
): Promise<TelephoneEncounter> {
  const te = emptyTelephoneEncounter({
    id: summary.id || generateUUIDv7(),
    companyId: input.companyId ?? null,
    facilityId: input.facilityId ?? null,
    facilityName: input.facilityName ?? null,
    patientId: input.patientId ?? null,
    ehrPatientId: summary.ehrPatientId ?? input.ehrPatientId ?? null,
    patientName: summary.patientName ?? input.patientName ?? null,
    reasonCode: summary.reasonCode ?? null,
    reasonText: summary.reasonText ?? '',
    providerId: summary.providerId ?? null,
    providerName: summary.providerName ?? null,
    message: summary.message ?? '',
    assignedToId: summary.assignedToId ?? null,
    assignedToName: summary.assignedToName ?? null,
    status: 'open',
    actions: Array.isArray(summary.actions) ? summary.actions : [],
    ehrEncounterId: summary.ehrEncounterId ?? null,
    // Carry the just-completed call so the adopted encounter can draft its
    // Action Taken note from that transcript (and embed its transcript).
    sourceCallId: input.sourceCallId ?? null,
    translator: input.translator ?? null,
    createdBy: input.createdBy ?? null,
    // Prefer ECW's record of who opened the TE — the adopter is an editor,
    // not the creator.
    createdByName: summary.createdByName ?? input.createdByName ?? null,
    createdAt: summary.createdAt ?? nowIso(),
  });
  store.set(te.id, te);
  return clone(te);
}

// ── ECW attribution stamps ──────────────────────────────────────────────────

interface AuthorStampInput {
  name?: string | null;
  role?: string | null;
}

// "— Dr. Lam, Provider · 07/19/2026, 9:41 PM". ECW's telephone-encounter
// fields are single free-text blobs with no author metadata (nothing we send
// round-trips as structured authorship), so the who + date/time ride inside
// the text itself. Only the wire payload is stamped — the local record and
// the Action Taken thread stay clean, since they carry structured
// author/createdAt fields already.
function buildEcwAttributionStamp(
  author: AuthorStampInput | null | undefined,
  at?: string | Date,
): string {
  const name = (author?.name || '').trim();
  if (!name) return '';
  const role = (author?.role || '').trim();
  const parsed = at instanceof Date ? at : at ? new Date(at) : new Date();
  const when = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const date = when.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const time = when.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `— ${role ? `${name}, ${role}` : name} · ${date}, ${time}`;
}

// Append the attribution stamp on its own line. Empty text stays empty (a
// bare stamp with no content would read as noise in the chart).
function stampEcwText(text: string, stamp: string): string {
  const trimmed = (text || '').trim();
  if (!trimmed || !stamp) return trimmed;
  return `${trimmed}\n${stamp}`;
}

// ── ECW update passthrough ───────────────────────────────────────────────────

// Persist an edit to an already-pushed/adopted TE (has an `ehrEncounterId`)
// via the write route, which routes to ECW's `updateTelephoneEncounter`. No-op
// for local-only drafts (nothing in ECW to update yet).
async function pushTelephoneEncounterUpdate(
  current: TelephoneEncounter,
  fields: Record<string, unknown>,
): Promise<void> {
  if (!current.ehrEncounterId) return;
  await request(`${EHR_PREFIX}/telephone-encounters/write`, {
    method: 'POST',
    body: JSON.stringify({
      ehrEncounterId: current.ehrEncounterId,
      ehrPatientId: current.ehrPatientId,
      ...fields,
    }),
  });
}

// ── Encounter writes (local editing session) ────────────────────────────────

export async function saveTelephoneEncounter(
  te: TelephoneEncounter,
): Promise<TelephoneEncounter> {
  const next: TelephoneEncounter = {
    ...clone(te),
    updatedAt: nowIso(),
  };
  store.set(next.id, next);
  return clone(next);
}

interface AddActionInput {
  text: string;
  authorId?: string | null;
  authorName?: string | null;
  authorRole?: string | null;
}

export async function addAction(
  id: string,
  input: AddActionInput,
): Promise<TelephoneEncounter> {
  const current = store.get(id);
  if (!current) throw new Error('Telephone encounter not found');
  const action: TelephoneEncounterAction = {
    id: generateUUIDv7(),
    authorId: input.authorId ?? null,
    authorName: input.authorName?.trim() || 'Staff',
    authorRole: input.authorRole ?? null,
    text: input.text.trim(),
    createdAt: nowIso(),
  };
  // Append to the ECW record FIRST when this TE exists in the EHR. If that
  // write fails (e.g. the Zatanna `updateTelephoneEncounter` op rejects), we
  // throw so the caller can tell the user — rather than adding a phantom local
  // action that was never persisted to ECW. Local-only drafts (no
  // ehrEncounterId) skip the push and just record the action locally.
  // `sourceCallId` rides along when this TE was adopted for a phone call: an
  // Action Taken IS that call's documentation, so the backend checks the call
  // off the charting queue (stamps `ehr_pushed_at` on the Conversation).
  // The pushed text carries the author + date/time stamp (ECW's Action Taken
  // is a bare free-text field); the local thread keeps the clean text since
  // it renders authorship from the structured fields.
  try {
    await pushTelephoneEncounterUpdate(current, {
      actionTaken: stampEcwText(
        action.text,
        buildEcwAttributionStamp(
          {name: action.authorName, role: action.authorRole},
          action.createdAt,
        ),
      ),
      ...(current.sourceCallId ? {sourceCallId: current.sourceCallId} : {}),
    });
  } catch (err) {
    log.error('add_action_failed', {
      te_id: id,
      ehr_encounter_id: current.ehrEncounterId || null,
      action_length: action.text.length,
      ...describeError(err),
    });
    throw err;
  }
  const next: TelephoneEncounter = {
    ...clone(current),
    actions: [...current.actions.map(a => ({...a})), action],
    updatedAt: nowIso(),
  };
  store.set(id, next);
  log.info('add_action_succeeded', {
    te_id: id,
    ehr_encounter_id: current.ehrEncounterId || null,
    action_count: next.actions.length,
    pushed_to_ehr: Boolean(current.ehrEncounterId),
  });
  return clone(next);
}

// Change the eCW facility a telephone encounter is filed under. In edit mode
// (the TE already lives in ECW) this pushes an `updateTelephoneEncounter` with
// the new `facilityId` first, then updates locally; on failure it throws so the
// caller can revert the optimistic selection. For a local-only draft it just
// records the choice, which is sent with the create payload at push time.
export async function setFacility(
  id: string,
  facility: FacilityOption | null,
): Promise<TelephoneEncounter> {
  const current = store.get(id);
  if (!current) throw new Error('Telephone encounter not found');
  const next: TelephoneEncounter = {
    ...clone(current),
    facilityId: facility?.id ?? null,
    facilityName: facility?.name ?? null,
    updatedAt: nowIso(),
  };
  try {
    await pushTelephoneEncounterUpdate(next, {
      facilityId: facility?.id ?? '',
      facilityName: facility?.name ?? '',
    });
  } catch (err) {
    log.error('set_facility_failed', {
      te_id: id,
      ehr_encounter_id: current.ehrEncounterId || null,
      facility_id: facility?.id ?? null,
      ...describeError(err),
    });
    throw err;
  }
  store.set(id, next);
  log.info('set_facility_succeeded', {
    te_id: id,
    ehr_encounter_id: current.ehrEncounterId || null,
    facility_id: facility?.id ?? null,
    cleared: !facility,
  });
  return clone(next);
}

export async function assignTo(
  id: string,
  member: CareTeamMember | null,
): Promise<TelephoneEncounter> {
  const current = store.get(id);
  if (!current) throw new Error('Telephone encounter not found');
  const next: TelephoneEncounter = {
    ...clone(current),
    assignedToId: member?.id ?? null,
    assignedToName: member?.name ?? null,
    updatedAt: nowIso(),
  };
  // Reassign in ECW first; if it fails, throw so the caller can revert the
  // optimistic selection and tell the user. No-op for local-only drafts (the
  // assignee is sent with the create payload at push time instead).
  try {
    await pushTelephoneEncounterUpdate(next, {
      assignedToId: member?.id ?? '',
      assignedToName: member?.name ?? '',
    });
  } catch (err) {
    log.error('assign_failed', {
      te_id: id,
      ehr_encounter_id: current.ehrEncounterId || null,
      assignee_id: member?.id ?? null,
      ...describeError(err),
    });
    throw err;
  }
  store.set(id, next);
  log.info('assign_succeeded', {
    te_id: id,
    ehr_encounter_id: current.ehrEncounterId || null,
    assignee_id: member?.id ?? null,
    cleared: !member,
  });
  return clone(next);
}

// "Push" a reviewed draft to the EHR. Writes the TE fields to ECW via the
// backend, then flips the local record to `open` on success. The backend
// write op is scaffolded against a pending Zatanna op (`// TODO(zatanna)`),
// so this surfaces a real error until that op is wired — the local record
// stays a draft on failure.
//
// `options.author` is the pushing user; on the initial create the Message
// pushed to ECW gets the "— name, role · date, time" attribution stamp (the
// local record keeps the clean text). Updates never re-stamp.
export async function pushToEhr(
  id: string,
  options: {author?: AuthorStampInput | null} = {},
): Promise<TelephoneEncounter> {
  const current = store.get(id);
  if (!current) throw new Error('Telephone encounter not found');

  const startedAt = Date.now();
  log.info('push_to_ehr_started', {
    te_id: id,
    ehr_patient_id: current.ehrPatientId || null,
    reason_code: current.reasonCode || null,
    has_message: Boolean(current.message),
    has_assignee: Boolean(current.assignedToId),
    is_update: Boolean(current.ehrEncounterId),
  });

  const messageForEhr = current.ehrEncounterId
    ? current.message
    : stampEcwText(
        current.message,
        buildEcwAttributionStamp(options.author, new Date()),
      );

  let data;
  try {
    data = await request(`${EHR_PREFIX}/telephone-encounters/write`, {
      method: 'POST',
      body: JSON.stringify({
        ehrPatientId: current.ehrPatientId,
        ehrEncounterId: current.ehrEncounterId,
        facilityId: current.facilityId,
        facilityName: current.facilityName,
        reasonCode: current.reasonCode,
        reasonText: current.reasonText,
        providerId: current.providerId,
        providerName: current.providerName,
        message: messageForEhr,
        assignedToId: current.assignedToId,
        assignedToName: current.assignedToName,
        sourceCallId: current.sourceCallId,
      }),
    });
  } catch (err) {
    log.error('push_to_ehr_failed', {
      te_id: id,
      ehr_patient_id: current.ehrPatientId || null,
      reason_code: current.reasonCode || null,
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    throw err;
  }

  const next: TelephoneEncounter = {
    ...clone(current),
    // Capture the ECW encounter id so subsequent edits update (not recreate) it.
    ehrEncounterId: data?.encounterId ?? current.ehrEncounterId,
    status: 'open',
    updatedAt: nowIso(),
  };
  store.set(id, next);
  log.info('push_to_ehr_succeeded', {
    te_id: id,
    ehr_patient_id: current.ehrPatientId || null,
    ehr_encounter_id: next.ehrEncounterId || null,
    reason_code: current.reasonCode || null,
    duration_ms: Date.now() - startedAt,
  });
  return clone(next);
}

// "Address & close" — locks the record. Held locally for now; closing an open
// TE in ECW will be a follow-on Zatanna op.
export async function closeTelephoneEncounter(
  id: string,
): Promise<TelephoneEncounter> {
  const current = store.get(id);
  if (!current) throw new Error('Telephone encounter not found');
  // Address/close in ECW first; if it fails, surface the error (don't claim
  // closed). Local-only drafts with no ehrEncounterId just close locally.
  try {
    await pushTelephoneEncounterUpdate(current, {address: true});
  } catch (err) {
    log.error('close_failed', {
      te_id: id,
      ehr_encounter_id: current.ehrEncounterId || null,
      ...describeError(err),
    });
    throw err;
  }
  const timestamp = nowIso();
  const next: TelephoneEncounter = {
    ...clone(current),
    status: 'closed',
    closedAt: timestamp,
    updatedAt: timestamp,
  };
  store.set(id, next);
  log.info('close_succeeded', {
    te_id: id,
    ehr_encounter_id: current.ehrEncounterId || null,
    action_count: next.actions.length,
  });
  return clone(next);
}

export const telephoneEncounterService = {
  getReasonPresets,
  getFacilities,
  getProviders,
  getCareTeam,
  getAssignableStaff,
  getTelephoneEncounter,
  findOpenTelephoneEncounters,
  generateTelephoneEncounterText,
  editTelephoneEncounterText,
  simplifyTelephoneEncounterMessage,
  createBlankDraft,
  createDraftFromCall,
  adoptOpenTelephoneEncounter,
  saveTelephoneEncounter,
  addAction,
  assignTo,
  setFacility,
  pushToEhr,
  closeTelephoneEncounter,
};

export default telephoneEncounterService;
